import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  PlusCircle, ShoppingBag, Truck, MessageCircle, User, ChevronLeft, ChevronRight, X,
} from "lucide-react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

import { RootState } from "../../../redux/store";
import logo from "../../../assets/app/logo.png";

import GroceryProducts       from "./GroceryProducts";

// Same self-contained chat panel used in FoodDashboard.
import IMessagePreview from "../../../MESSAGES/IMessagePreview";
import CommerceDeliveryZones from "../../../SUPERAPP/DASHBOARD/CommerceDeliveryZones";
import HostProductOrders from "../../../SUPERAPP/DASHBOARD/HostProductOrders";

/* ═══════════════════════════════════════════════════════════
   GROCERY DESIGN TOKENS — same palette as GroceriesHome
   (green → saffron → red), swapped in for the red/gold HEART set
═══════════════════════════════════════════════════════════ */
const GROCE = {
  bg:        "#F7F6F2",
  panelBg:   "#FFFFFF",
  headerBg:  "rgba(255,255,255,0.97)",
  surface:   "#EEECE6",

  dark:      "#14180F",
  textMuted: "#727868",
  textFaint: "#A8AC9E",

  red:       "#264D0E",   // primary accent (was red) — now forest green
  redDark:   "#1B3A0A",
  redSoft:   "#EEF5E8",

  gold:      "#D97706",   // secondary accent (was gold) — now saffron
  goldDark:  "#B45F04",
  goldSoft:  "#FCEFD4",

  border:    "#E4E1D6",
  divider:   "#EDEAE0",
  danger:    "#C0392B",
  success:   "#1FAA59",
};

const GRAD      = `linear-gradient(135deg, ${GROCE.red}, ${GROCE.gold})`;
const GRAD_H    = `linear-gradient(90deg, ${GROCE.red}, ${GROCE.gold})`;
const GRAD_RING = `linear-gradient(90deg, ${GROCE.dark}, ${GROCE.red}, ${GROCE.gold}, ${GROCE.red}, ${GROCE.dark})`;

const SIDEBAR_W           = 240;
const SIDEBAR_W_COLLAPSED = 72;
const TABBAR_H             = 64;

/* ═══════════════════════════════════════════════════════════
   GLOBAL CSS
═══════════════════════════════════════════════════════════ */
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Syne:wght@600;700;800&family=DM+Sans:wght@400;500;600;700&display=swap');
  *,::before,::after{box-sizing:border-box;margin:0;padding:0;}
  body{background:${GROCE.bg};}
  button,input{font-family:inherit;}
  ::-webkit-scrollbar{width:3px;}
  ::-webkit-scrollbar-thumb{background:${GROCE.border};border-radius:99px;}

  @keyframes gradSpin{0%{background-position:0% 50%;}50%{background-position:100% 50%;}100%{background-position:0% 50%;}}
  @keyframes pulseDot{0%,100%{opacity:1;}50%{opacity:.4;}}
  @keyframes fadeSlide{from{opacity:0;transform:translateY(8px);}to{opacity:1;transform:translateY(0);}}

  .sd-nav{
    width:100%;display:flex;align-items:center;gap:12px;
    padding:10px 12px;border:none;background:transparent;
    cursor:pointer;transition:background .15s;font-family:inherit;
    border-radius:12px;text-align:left;position:relative;z-index:1;
  }
  .sd-nav:hover{background:${GROCE.goldSoft};}
  .sd-nav.on{
    background:linear-gradient(135deg, ${GROCE.red}, ${GROCE.redDark});
    box-shadow:0 6px 16px rgba(38,77,14,.32);
  }
  .sd-nav.collapsed{justify-content:center;padding:10px;}

  .sd-navicon{
    width:34px;height:34px;border-radius:10px;flex-shrink:0;
    background:${GROCE.goldSoft};display:flex;align-items:center;justify-content:center;
    transition:background .15s,color .15s;
  }
  .sd-nav.on .sd-navicon{background:rgba(255,255,255,.18);}

  .sd-navlabel{white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}

  .sd-logout{
    display:flex;align-items:center;gap:12px;width:100%;background:transparent;border:none;
    cursor:pointer;padding:10px 12px;border-radius:12px;color:${GROCE.danger};font-weight:700;
    font-size:13px;text-align:left;transition:background .15s;font-family:inherit;
  }
  .sd-logout:hover{background:#FBE7E9;}
  .sd-logout.collapsed{justify-content:center;padding:10px;}

  .sd-collapse-toggle{
    display:flex;align-items:center;justify-content:center;width:30px;height:30px;border-radius:9px;
    background:${GROCE.goldSoft};border:none;color:${GROCE.redDark};cursor:pointer;flex-shrink:0;
    transition:background .15s,color .15s;
  }
  .sd-collapse-toggle:hover{background:${GROCE.gold};color:#fff;}

  .sd-icon{
    width:34px;height:34px;border:1px solid transparent;
    background:transparent;display:flex;align-items:center;
    justify-content:center;cursor:pointer;transition:all .15s;
    border-radius:9px;
  }
  .sd-icon:hover{background:${GROCE.goldSoft};border-color:rgba(38,77,14,.25);}

  .page-watermark{
    position:fixed;top:50%;left:50%;
    width:min(58vw,540px);height:min(58vw,540px);
    transform:translate(-50%,-50%);
    opacity:.035;pointer-events:none;z-index:0;object-fit:contain;
  }
  .sidebar-watermark{
    position:absolute;left:50%;bottom:70px;
    width:140px;height:140px;transform:translateX(-50%) rotate(-6deg);
    opacity:.045;pointer-events:none;z-index:0;object-fit:contain;
  }

  /* ── mobile bottom tab bar ── */
  .sd-tabbar{
    position:fixed;left:0;right:0;bottom:0;display:flex;
    background:${GROCE.panelBg};border-top:1px solid ${GROCE.border};
    z-index:997;padding-bottom:env(safe-area-inset-bottom);
    box-shadow:0 -4px 18px rgba(20,24,15,0.07);
  }
  .sd-tab{
    flex:1;display:flex;flex-direction:column;align-items:center;justify-content:center;
    gap:3px;padding:8px 4px 10px;background:transparent;border:none;cursor:pointer;
    color:${GROCE.textFaint};transition:color .15s;position:relative;
  }
  .sd-tab.active{color:${GROCE.red};}
  .sd-tab span{font-size:10.5px;font-weight:700;font-family:'DM Sans',sans-serif;}
  .sd-tab-iconwrap{
    width:26px;height:26px;border-radius:8px;display:flex;align-items:center;justify-content:center;
    transition:background .15s;
  }
  .sd-tab.active .sd-tab-iconwrap{background:${GROCE.goldSoft};}
  .sd-tab-dot{
    position:absolute;top:4px;width:4px;height:4px;border-radius:50%;
    background:${GROCE.red};animation:pulseDot 2s infinite;
  }
`;

/* ── nav config ──
   "tab" items swap the main content area.
   "chat" toggles the IMessagePreview panel — a centered modal on desktop,
   an anchored dropdown on mobile — rather than navigating anywhere.
   "profile" is a hard navigate to /Profile/:userId. */
type TabKey = "add" | "orders" | "delivery";
type NavKind = "tab" | "chat" | "profile";
interface NavItem {
  id: TabKey | "chat" | "profile";
  label: string;
  icon: React.ElementType;
  kind: NavKind;
}
const NAV: NavItem[] = [
  { id: "add",      label: "Products",       icon: PlusCircle,    kind: "tab"     },
  { id: "orders",   label: "Orders",         icon: ShoppingBag,   kind: "tab"     },
  { id: "delivery", label: "Delivery Zones", icon: Truck,         kind: "tab"     },
  { id: "chat",     label: "Chat",           icon: MessageCircle, kind: "chat"    },
  { id: "profile",  label: "Profile",        icon: User,          kind: "profile" },
];

/* ═══════════════════════════════════════════════════════════
   MAIN
═══════════════════════════════════════════════════════════ */
const GroceryDashboard: React.FC = () => {
  const navigate = useNavigate();
  const params   = useParams<{ userId?: string }>();
  const authUser = useSelector((s: RootState) => s.auth.user);
  const userId   = params.userId || authUser?.userId;

  const [activeTab, setActiveTab] = useState<TabKey>("add");
  const [mobile,    setMobile]    = useState(window.innerWidth < 1024);
  const [collapsed, setCollapsed] = useState(false); // desktop sidebar collapse

  // Chat panel — centered modal on desktop (sidebar clips absolute
  // dropdowns via overflow:hidden), anchored dropdown on mobile tab bar.
  const [chatOpen, setChatOpen] = useState(false);
  const chatRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!authUser) { navigate("/login"); return; }
    if (!userId)   { navigate("/"); }
  }, [authUser, userId, navigate]);

  useEffect(() => {
    const fn = () => setMobile(window.innerWidth < 1024);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);

  // Outside-click-to-close only applies to the mobile anchored dropdown —
  // the desktop modal already has its own full-screen backdrop click handler.
  useEffect(() => {
    if (!chatOpen || !mobile) return;
    const fn = (e: MouseEvent) => {
      if (chatRef.current && !chatRef.current.contains(e.target as Node)) setChatOpen(false);
    };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, [chatOpen, mobile]);

  const navClick = (item: NavItem) => {
    if (item.kind === "chat") {
      setChatOpen(v => !v);
      return;
    }
    if (item.kind === "profile") {
      navigate(`/Profile/${userId}`);
      return;
    }
    setActiveTab(item.id as TabKey);
  };

  const renderMain = () => {
    switch (activeTab) {
      case "add":      return <GroceryProducts/>;
      case "orders":   return <HostProductOrders/>;
      case "delivery": return <CommerceDeliveryZones/>;
      default:         return null;
    }
  };

  const initial = authUser?.email?.charAt(0)?.toUpperCase() ?? "A";
  const currentSidebarW = collapsed ? SIDEBAR_W_COLLAPSED : SIDEBAR_W;

  /* ═══ RENDER ═══ */
  return (
    <div style={{
      minHeight: "100vh", background: GROCE.bg,
      fontFamily: "'DM Sans',sans-serif",
      color: GROCE.dark, display: "flex", position: "relative", overflow: "hidden",
    }}>
      <style>{CSS}</style>

      <img src={logo} alt="" className="page-watermark" />

      {/* ══════════════════════════
          SIDEBAR — desktop only
      ══════════════════════════ */}
      {!mobile && (
        <aside style={{
          width: currentSidebarW,
          background: GROCE.panelBg,
          borderRight: `1px solid ${GROCE.border}`,
          display: "flex", flexDirection: "column",
          position: "sticky",
          top: 0, height: "100vh", zIndex: 999,
          transition: "width .18s ease",
          boxShadow: "1px 0 0 rgba(217,119,6,.14)",
          flexShrink: 0,
          overflow: "hidden",
        }}>
          <img src={logo} alt="" className="sidebar-watermark" />

          <div style={{ height: 3, background: GRAD_RING, backgroundSize: "300% 100%",
            animation: "gradSpin 4s linear infinite", position: "relative", zIndex: 1 }}/>

          {/* logo block */}
          <div style={{
            padding: "18px 16px 16px",
            borderBottom: `1px solid ${GROCE.border}`,
            display: "flex", alignItems: "center", justifyContent: "space-between",
            position: "relative", zIndex: 1, background: GROCE.panelBg,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
              <div style={{ position: "relative", display: "inline-flex", flexShrink: 0 }}>
                <div style={{
                  position: "absolute", inset: -2, borderRadius: "50%",
                  background: GRAD_RING, backgroundSize: "300% 100%",
                  animation: "gradSpin 3s linear infinite", zIndex: 0,
                }}/>
                <div style={{
                  position: "relative", zIndex: 1, background: GROCE.panelBg,
                  borderRadius: "50%", padding: 2, display: "inline-flex",
                }}>
                  <img src={logo} alt="Heartlas"
                    style={{ width: 28, height: 28, borderRadius: "50%",
                      objectFit: "cover", display: "block" }}/>
                </div>
              </div>

              {!collapsed && (
                <div style={{ minWidth: 0 }}>
                  <div style={{
                    fontSize: 13, fontWeight: 900, letterSpacing: 1.2,
                    background: GRAD_RING, backgroundSize: "300% 100%",
                    animation: "gradSpin 4s linear infinite",
                    WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
                    fontFamily: "'Syne',sans-serif", whiteSpace: "nowrap",
                  }}>
                    HEARTLAS
                  </div>
                  <div style={{ fontSize: 8, color: GROCE.textFaint,
                    letterSpacing: "0.18em", marginTop: 1 }}>
                    GROCERY STUDIO
                  </div>
                </div>
              )}
            </div>

            <button
              className="sd-collapse-toggle"
              onClick={() => setCollapsed((p) => !p)}
              title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              {collapsed ? <ChevronRight size={15} /> : <ChevronLeft size={15} />}
            </button>
          </div>

          {/* navigation */}
          <nav style={{ flex: 1, overflowY: "auto", padding: "12px 10px", position: "relative", zIndex: 1 }}>
            {NAV.map((item) => {
              const { id, label, icon: Icon, kind } = item;
              const on = kind === "tab" && activeTab === id;
              const isChat = kind === "chat";
              const highlighted = on || (isChat && chatOpen);
              return (
                <div key={id} style={{ marginBottom: 4 }}>
                  <button
                    className={`sd-nav${highlighted ? " on" : ""}${collapsed ? " collapsed" : ""}`}
                    onClick={() => navClick(item)}
                    title={label}
                  >
                    <div className="sd-navicon">
                      <Icon size={16} color={highlighted ? "#fff" : GROCE.red} strokeWidth={highlighted ? 2 : 1.7}/>
                    </div>
                    {!collapsed && (
                      <span style={{
                        fontSize: 13.5, fontWeight: highlighted ? 800 : 600,
                        color: highlighted ? "#fff" : GROCE.dark,
                        flex: 1, letterSpacing: highlighted ? -0.1 : 0,
                      }} className="sd-navlabel">
                        {label}
                      </span>
                    )}
                    {highlighted && !collapsed && (
                      <div style={{
                        width: 6, height: 6, borderRadius: "50%",
                        background: "#fff", flexShrink: 0,
                        animation: "pulseDot 2s infinite",
                      }}/>
                    )}
                  </button>
                </div>
              );
            })}
          </nav>

          {/* sidebar footer */}
          <div style={{ padding: "14px 16px",
            borderTop: `1px solid ${GROCE.border}`, position: "relative", zIndex: 1, background: GROCE.panelBg,
            display: "flex", flexDirection: "column", gap: 8 }}>
            {!collapsed && (
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{
                  width: 28, height: 28, borderRadius: "50%", flexShrink: 0,
                  background: GRAD_H, display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <span style={{ fontSize: 11, fontWeight: 900, color: "#fff" }}>{initial}</span>
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: GROCE.dark,
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {authUser?.firstName || authUser?.displayName || "Seller"}
                  </div>
                  <div style={{ fontSize: 9, color: GROCE.textFaint,
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {authUser?.email || ""}
                  </div>
                </div>
              </div>
            )}
            <button className={`sd-logout${collapsed ? " collapsed" : ""}`}
              onClick={() => navigate("/logout")} title="Log out">
              <span style={{ display: "inline-flex", transform: "rotate(45deg)", fontSize: 15, lineHeight: 1 }}>+</span>
              {!collapsed && <span>Log out</span>}
            </button>
          </div>
        </aside>
      )}

      {/* ══════════════════════════
          MAIN CONTENT — no topbar, content starts flush
      ══════════════════════════ */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0, position: "relative", zIndex: 1 }}>
        <main style={{
          flex: 1, overflowY: "auto", background: "transparent",
          paddingBottom: mobile ? TABBAR_H + 12 : 0,
        }}>
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.2, ease: [0.2, 0, 0.1, 1] }}
              style={{ minHeight: "100%" }}
            >
              {renderMain()}
            </motion.div>
          </AnimatePresence>
        </main>
      </div>

      {/* ══════════════════════════
          DESKTOP CHAT MODAL — centered on screen, escapes the
          sidebar's overflow:hidden by living at the top level of the tree.
      ══════════════════════════ */}
      {!mobile && (
        <AnimatePresence>
          {chatOpen && (
            <>
              <motion.div
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                onClick={() => setChatOpen(false)}
                style={{ position: "fixed", inset: 0, background: "rgba(20,24,15,.45)", zIndex: 1000, backdropFilter: "blur(4px)" }}
              />
              <motion.div
                initial={{ opacity: 0, scale: 0.94, y: 16 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.94, y: 16 }}
                transition={{ type: "spring", stiffness: 340, damping: 30 }}
                style={{
                  position: "fixed", top: "50%", left: "50%", transform: "translate(-50%,-50%)",
                  zIndex: 1001, width: "min(480px, calc(100vw - 48px))",
                  maxHeight: "min(640px, calc(100vh - 80px))", overflowY: "auto",
                  background: "#fff", borderRadius: 20,
                  boxShadow: "0 30px 80px rgba(20,24,15,.3)", border: `1.5px solid ${GROCE.border}`,
                }}
              >
                <div style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  padding: "16px 20px", borderBottom: `1px solid ${GROCE.border}`,
                }}>
                  <span style={{ fontSize: 15, fontWeight: 700, color: GROCE.dark, fontFamily: "'Syne',sans-serif" }}>
                    Chat
                  </span>
                  <button onClick={() => setChatOpen(false)} style={{ background: "none", border: "none", cursor: "pointer", color: GROCE.textFaint }}>
                    <X size={18} />
                  </button>
                </div>
                <IMessagePreview />
              </motion.div>
            </>
          )}
        </AnimatePresence>
      )}

      {/* ══════════════════════════
          MOBILE BOTTOM TAB BAR — Chat stays an anchored dropdown here
      ══════════════════════════ */}
      {mobile && (
        <div className="sd-tabbar">
          {NAV.map((item) => {
            const { id, label, icon: Icon, kind } = item;
            const on = kind === "tab" && activeTab === id;
            const isChat = kind === "chat";
            const highlighted = on || (isChat && chatOpen);
            return (
              <div key={id} style={{ position: "relative", flex: 1, display: "flex" }}>
                <button
                  className={`sd-tab${highlighted ? " active" : ""}`}
                  onClick={() => navClick(item)}
                >
                  {highlighted && <div className="sd-tab-dot" />}
                  <div className="sd-tab-iconwrap">
                    <Icon size={18} color={highlighted ? GROCE.red : GROCE.textFaint} strokeWidth={highlighted ? 2 : 1.7} />
                  </div>
                  <span>{label}</span>
                </button>

                {isChat && (
                  <AnimatePresence>
                    {chatOpen && (
                      <>
                        <div
                          style={{ position: "fixed", inset: 0, zIndex: 998 }}
                          onClick={() => setChatOpen(false)}
                        />
                        <motion.div
                          ref={chatRef}
                          initial={{ opacity: 0, y: 8, scale: 0.97 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: 8, scale: 0.97 }}
                          transition={{ duration: 0.15 }}
                          style={{
                            position: "fixed", right: 12,
                            bottom: TABBAR_H + 12,
                            width: "min(360px, calc(100vw - 24px))",
                            maxHeight: "60vh", overflowY: "auto",
                            background: "#fff", border: `1.5px solid ${GROCE.border}`,
                            borderRadius: 16, boxShadow: "0 20px 50px rgba(20,24,15,.18)",
                            zIndex: 999,
                          }}
                        >
                          <IMessagePreview />
                        </motion.div>
                      </>
                    )}
                  </AnimatePresence>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default GroceryDashboard;
